import { format, intervalToDuration, isValid, parse } from 'date-fns';
import EventEmitter from 'eventemitter3';

export const MIN_ZOOM_LEVEL = 1;
export const MAX_ZOOM_LEVEL = 25;

// We don't have per-asset frame rates available in the trimmer, so we assume
// 30fps. Trim boundaries snap to whole frames at this rate so a trim always
// lands on a real frame rather than an arbitrary sub-frame time.
export const ASSUMED_FRAME_RATE = 30;
export const FRAME_DURATION_MILLIS = 1000 / ASSUMED_FRAME_RATE;

/**
 * Snap a time (in milliseconds) to the nearest whole video frame at the
 * assumed frame rate. Returns whole milliseconds.
 */
const snapMillisToFrame = (millis: number) => {
    const frame = Math.round(millis / FRAME_DURATION_MILLIS);
    return Math.round(frame * FRAME_DURATION_MILLIS);
};

/**
 * Snap a time (in milliseconds) to the nearest whole video frame while keeping
 * the result within `[minMillis, maxMillis]`. If the nearest frame overshoots
 * `maxMillis` (e.g. an asset whose duration isn't frame-aligned), fall back to
 * the previous frame; if it undershoots `minMillis`, advance to the next frame.
 * This lets callers snap boundaries without re-introducing values that upstream
 * clamps just removed.
 */
export const snapMillisToFrameWithin = (millis: number, minMillis: number, maxMillis: number) => {
    let snapped = snapMillisToFrame(millis);
    if (snapped > maxMillis) {
        snapped = Math.round(Math.floor(maxMillis / FRAME_DURATION_MILLIS) * FRAME_DURATION_MILLIS);
    }
    if (snapped < minMillis) {
        snapped = Math.round(Math.ceil(minMillis / FRAME_DURATION_MILLIS) * FRAME_DURATION_MILLIS);
    }
    return snapped;
};

// Time format patterns for use with date-fns.
// The fractional-second formats use `.SS` (hundredths) rather than `.S`
// (tenths) so they can display and accept frame-accurate times at 30fps
// (~33ms/frame), which tenths (~10fps) could not represent.
export enum TimeFormat {
    HH_MM_SS = 'HH:mm:ss',
    HH_MM_SS_S = 'HH:mm:ss.SS',
    MM_SS = 'mm:ss',
    MM_SS_S = 'mm:ss.SS',
    M_SS = 'm:ss',
    M_SS_S = 'm:ss.SS',
}

export const tour_emitter = new EventEmitter();

export const startTour = () => {
    tour_emitter.emit('nexttour');
};

export const getMillisFromSeconds = (value: number) => {
    return value * 1000;
};

export const getSecondsFromMillis = (value: number) => {
    return value / 1000;
};

export const isValidTimeString = (value: string, formatStr = TimeFormat.HH_MM_SS_S) => {
    // We need to add a date part to make parse work with just time values
    const dateFormat = `yyyy-MM-dd'T'${formatStr}`;
    try {
        // Prefix with a date part
        const dateStr = `2000-01-01T${value}`;
        const date = parse(dateStr, dateFormat, new Date());
        return isValid(date);
    } catch (e) {
        return false;
    }
};

/**
 * Format time string from milliseconds
 */
export const getTimeStringFromMillis = (millis: number, formatStr = TimeFormat.HH_MM_SS_S) => {
    /*
    the date-fns format function only works with dates in the local timezone.
    We make a date object in UTC, adjusted for local timezone offset, and then format it.
    */
    millis = Math.max(0, millis);
    const date = new Date(millis);
    const localOffsetMillis = date.getTimezoneOffset() * 60 * 1000;

    const adjustedMillis = millis + localOffsetMillis;
    const adjustedDate = new Date(adjustedMillis);
    const utcDate = new Date(
        Date.UTC(
            1970,
            0,
            1,
            adjustedDate.getUTCHours(),
            adjustedDate.getUTCMinutes(),
            adjustedDate.getUTCSeconds(),
            adjustedDate.getUTCMilliseconds(),
        ),
    );
    return format(utcDate, formatStr);
};

export const getTimeStringFromSeconds = (seconds: number, format = TimeFormat.HH_MM_SS_S) => {
    return getTimeStringFromMillis(getMillisFromSeconds(seconds), format);
};

export const getMillisFromTimeString = (value: string, formatStr = TimeFormat.HH_MM_SS_S) => {
    if (!isValidTimeString(value, formatStr)) {
        throw new Error(`Time string ${value} does not match format: ${formatStr}`);
    }

    // Parse the time string with a date prefix
    const dateFormat = `yyyy-MM-dd'T'${formatStr}`;
    const dateStr = `2000-01-01T${value}`;
    const date = parse(dateStr, dateFormat, new Date());

    // Extract hours, minutes, seconds, milliseconds
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();
    const milliseconds = date.getMilliseconds();

    return (hours * 60 * 60 + minutes * 60 + seconds) * 1000 + milliseconds;
};

export const getSecondsFromTimeString = (value: string, format = TimeFormat.HH_MM_SS_S) => {
    return getMillisFromTimeString(value, format) / 1000;
};

export const getReadableTimeFromMillis = (millis: number, separator = ' ') => {
    if (millis < 0) {
        throw new Error(`Invalid time value: ${millis}`);
    }

    const duration = intervalToDuration({ start: 0, end: millis });
    const { hours, minutes, seconds } = duration;
    // date-fns Duration doesn't include milliseconds by default
    const millisPart = Math.floor(millis % 1000);

    if (hours && hours > 1) {
        return `${hours}${separator}hours`;
    }
    if (hours && hours === 1) {
        return `${hours}${separator}hour`;
    }
    if (minutes && minutes > 1) {
        return `${minutes}${separator}minutes`;
    }
    if (minutes && minutes === 1) {
        return `${minutes}${separator}minute`;
    }
    if (seconds && seconds > 1) {
        return `${seconds}${separator}seconds`;
    }
    if (seconds && seconds === 1) {
        return `${seconds}${separator}second`;
    }
    if (millisPart > 1) {
        return `${millisPart}${separator}milliseconds`;
    }
    if (millisPart === 1) {
        return `${millisPart}${separator}millisecond`;
    }

    throw new Error(`Invalid time value: ${millis}`);
};

export const getTimelineWidthMultiplier = (zoom: number) => {
    if (zoom < MIN_ZOOM_LEVEL) {
        throw new Error(`Invalid zoom level: ${zoom}`);
    }
    if (zoom > MAX_ZOOM_LEVEL) {
        throw new Error(`Invalid zoom level: ${zoom}`);
    }
    return (50 * (zoom + 1)) / 100;
};

export const getTimelineWidth = (zoom: number) => {
    const multiplier = getTimelineWidthMultiplier(zoom);
    const percentWidth = 100 * multiplier;
    return `${percentWidth}%`;
};

export enum TrimmerSource {
    TRIM = 'trim',
    UPLOAD = 'upload',
    SWAP = 'swap',
    CLIP_CREATOR = 'clip-creator',
}
